/**
 * SmartAddSlideModal - Modal for adding a slide with AI assistance
 *
 * Features:
 * - Text input for slide content instructions
 * - Visual layout picker with thumbnails
 * - "Smart" option (default) for AI to select best layout
 * - Triggers outline planner in EDIT mode after submission
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Sparkles,
  LayoutGrid,
  Check,
  Loader2,
} from 'lucide-react';
import type { TemplateLayout } from '@/types/outline';

export interface SmartAddSlideData {
  instructions: string;
  layoutId?: string;
  layoutName?: string;
}

interface SmartAddSlideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layouts: TemplateLayout[];
  onSubmit: (data: SmartAddSlideData) => Promise<void>;
  isSubmitting?: boolean;
}

// Special value for "Smart" selection
const SMART_LAYOUT_ID = '__smart__';

export const SmartAddSlideModal: React.FC<SmartAddSlideModalProps> = ({
  open,
  onOpenChange,
  layouts,
  onSubmit,
  isSubmitting = false,
}) => {
  const [instructions, setInstructions] = useState('');
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>(SMART_LAYOUT_ID);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setInstructions('');
      setSelectedLayoutId(SMART_LAYOUT_ID);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!instructions.trim()) return;

    const data: SmartAddSlideData = {
      instructions: instructions.trim(),
    };

    // Only include layout if user selected a specific one (not Smart)
    if (selectedLayoutId !== SMART_LAYOUT_ID) {
      const selectedLayout = layouts.find(l => l.layoutId === selectedLayoutId);
      if (selectedLayout) {
        data.layoutId = selectedLayout.layoutId;
        data.layoutName = selectedLayout.name;
      }
    }

    await onSubmit(data);
  };

  const isSmartSelected = selectedLayoutId === SMART_LAYOUT_ID;
  const canSubmit = instructions.trim().length > 0 && !isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-white/[0.08]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shadow-primary/20 bg-primary">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                Smart Add Slide
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 dark:text-white/50">
                Describe your slide and let AI generate the content
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-5 space-y-6">
            {/* Instructions Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-white/80">
                What should this slide contain?
              </label>
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Describe the content, topic, or key points for this slide..."
                className={cn(
                  "min-h-[120px] resize-none",
                  "bg-gray-50 dark:bg-white/[0.03]",
                  "border-gray-200 dark:border-white/[0.08]",
                  "focus:border-primary dark:focus:border-primary",
                  "focus:ring-2 focus:ring-primary/20",
                  "placeholder:text-gray-400 dark:placeholder:text-white/30"
                )}
                autoFocus
              />
              <p className="text-xs text-gray-500 dark:text-white/40">
                Be specific about the topic, key points, data, or message you want to convey.
              </p>
            </div>

            {/* Layout Picker — only shown when template has layouts */}
            {layouts.length > 0 && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 dark:text-white/80">
                Choose a layout
              </label>

              <div className="grid grid-cols-3 gap-3">
                {/* Smart Option (First) */}
                <button
                  type="button"
                  onClick={() => setSelectedLayoutId(SMART_LAYOUT_ID)}
                  className={cn(
                    "group relative flex flex-col rounded-xl overflow-hidden",
                    "border-2 transition-all duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-primary/50",
                    isSmartSelected
                      ? "border-primary ring-2 ring-primary/20 bg-accent dark:bg-primary/10"
                      : "border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                  )}
                >
                  {/* Smart Icon Area */}
                  <div className="aspect-video relative flex items-center justify-center bg-accent">
                    <div className="flex flex-col items-center gap-1">
                      <Sparkles className={cn(
                        "w-8 h-8",
                        isSmartSelected ? "text-primary" : "text-primary"
                      )} />
                    </div>

                    {/* Selected checkmark */}
                    {isSmartSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-lg">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Label */}
                  <div className="px-2 py-2 bg-gray-50 dark:bg-black/20">
                    <span className={cn(
                      "text-[11px] font-medium block text-center",
                      isSmartSelected ? "text-primary dark:text-accent-foreground" : "text-gray-600 dark:text-white/70"
                    )}>
                      Smart
                    </span>
                    <span className="text-[9px] text-gray-400 dark:text-white/30 block text-center">
                      AI selects best
                    </span>
                  </div>
                </button>

                {/* Layout Options */}
                {layouts.map((layout) => {
                  const isSelected = layout.layoutId === selectedLayoutId;

                  return (
                    <button
                      key={layout.id}
                      type="button"
                      onClick={() => setSelectedLayoutId(layout.layoutId)}
                      className={cn(
                        "group relative flex flex-col rounded-xl overflow-hidden",
                        "border-2 transition-all duration-200",
                        "focus:outline-none focus:ring-2 focus:ring-primary/50",
                        isSelected
                          ? "border-primary ring-2 ring-primary/20 bg-accent dark:bg-primary/5"
                          : "border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                      )}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-video bg-gray-100 dark:bg-zinc-900 relative">
                        {layout.thumbnail ? (
                          <img
                            src={layout.thumbnail}
                            alt={layout.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <LayoutGrid className="w-6 h-6 text-gray-300 dark:text-white/20" />
                          </div>
                        )}

                        {/* Selected checkmark */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-lg">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}

                        {/* Hover overlay */}
                        {!isSelected && (
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              Select
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Label */}
                      <div className="px-2 py-2 bg-gray-50 dark:bg-black/20">
                        <span className={cn(
                          "text-[11px] font-medium truncate block text-center",
                          isSelected ? "text-gray-900 dark:text-white" : "text-gray-600 dark:text-white/70"
                        )}>
                          {layout.name}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02]">
          <div className="flex items-center justify-end gap-3 w-full">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="text-gray-600 dark:text-white/60"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={cn(
                "bg-primary hover:bg-primary/90 text-primary-foreground",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Add Slide
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
